import { useEffect, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const CAT_FIELDS = [
  { key: "paint_labor_pct", label: "Paint labor" },
  { key: "plumbing_labor_pct", label: "Plumbing labor" },
  { key: "electrical_labor_pct", label: "Electrical labor" },
  { key: "hvac_labor_pct", label: "HVAC labor" },
  { key: "other_labor_pct", label: "Other labor" },
  { key: "materials_pct", label: "Materials" },
];

export default function AdminPmrBilling() {
  const [tab, setTab] = useState("default");
  return (
    <AdminShell>
      <div className="max-w-4xl mx-auto p-4 sm:p-6" data-testid="admin-pmr-billing">
        <header className="mb-4">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Rebill Config</div>
          <h1 className="font-display font-black text-3xl text-[#0B1F3A]">PMR Rebill Profile</h1>
          <p className="text-sm text-slate-600 mt-2">
            Set the markup % ProCall adds on top of vendor labor + materials. Portfolio-level
            overrides cascade over the company defaults.
          </p>
        </header>
        <nav className="flex gap-2 mb-5" data-testid="pmr-tabs">
          <Tab id="default" cur={tab} set={setTab}>Company Defaults</Tab>
          <Tab id="portfolios" cur={tab} set={setTab}>Portfolio Overrides</Tab>
        </nav>
        {tab === "default" && <TenantDefaults/>}
        {tab === "portfolios" && <PortfolioOverrides/>}
      </div>
    </AdminShell>
  );
}

function Tab({ id, cur, set, children }) {
  const active = id === cur;
  return (
    <button onClick={() => set(id)}
      data-testid={`pmr-tab-${id}`}
      className={`h-9 px-4 text-[11px] font-black uppercase tracking-widest border-2 ${
        active ? "bg-[#0f172a] text-white border-[#0f172a]" : "bg-white border-slate-300 text-slate-600 hover:border-[#0f172a]"
      }`}>
      {children}
    </button>
  );
}

function TenantDefaults() {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/pmr-billing/markup-profile").then(r => {
      const v = r.data;
      setP({
        ...v,
        min_charge_dollars: (v.min_charge_cents || 0) / 100,
        trip_charge_dollars: (v.trip_charge_cents || 0) / 100,
      });
    }).catch(() => toast.error("Couldn't load profile"));
  }, []);

  if (!p) return <div className="text-slate-500">Loading…</div>;

  const upd = (k, v) => setP(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...Object.fromEntries(CAT_FIELDS.map(f => [f.key, Number(p[f.key] || 0)])),
        min_charge_cents: Math.round(Number(p.min_charge_dollars || 0) * 100),
        client_split_enabled: !!p.client_split_enabled,
        trip_charge_cents: Math.round(Number(p.trip_charge_dollars || 0) * 100),
        split_labor_ratio: Number(p.split_labor_ratio || 0.8),
        split_materials_ratio: Number(p.split_materials_ratio || 0.2),
        ai_rewrite_descriptions: !!p.ai_rewrite_descriptions,
        company_name: p.company_name || "",
        company_line2: p.company_line2 || "",
        company_line3: p.company_line3 || "",
        remit_to: p.remit_to || "",
        notes_footer: p.notes_footer || "",
      };
      const r = await api.put("/pmr-billing/markup-profile", payload);
      setP({
        ...r.data,
        min_charge_dollars: (r.data.min_charge_cents || 0) / 100,
        trip_charge_dollars: (r.data.trip_charge_cents || 0) / 100,
      });
      toast.success("Company defaults saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Section title="Markup % by category">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {CAT_FIELDS.map(f => (
            <Field key={f.key} label={f.label + " %"}>
              <input type="number" min="0" max="500" step="1"
                value={p[f.key] ?? ""}
                onChange={e => upd(f.key, e.target.value)}
                data-testid={`default-${f.key}`}
                className={inputCls + " font-mono"}/>
            </Field>
          ))}
        </div>
        <div className="mt-3">
          <Field label="Minimum charge (dollars — 0 = disabled)">
            <input type="number" min="0" step="0.01"
              value={p.min_charge_dollars ?? 0}
              onChange={e => upd("min_charge_dollars", e.target.value)}
              data-testid="default-min-charge"
              className={inputCls + " sm:w-48 font-mono"}/>
          </Field>
        </div>
      </Section>

      <Section title="Client-Safe PDF Layout">
        <p className="text-xs text-slate-500 mb-3">
          Controls how the <b>Generate PMR Bill</b> PDF renders. Every vendor line
          is auto-split into three visible line items — Labor, Materials, and a
          Service Call charge — so no markup ever appears on the PDF you send out.
        </p>
        <div className="space-y-3">
          <label className="flex items-center gap-2" data-testid="default-split-enabled">
            <input type="checkbox" checked={!!p.client_split_enabled}
              onChange={e => upd("client_split_enabled", e.target.checked)}/>
            <span className="text-sm font-black">Split every line into Labor · Materials · Service Call</span>
          </label>
          <label className="flex items-center gap-2" data-testid="default-ai-rewrite">
            <input type="checkbox" checked={!!p.ai_rewrite_descriptions}
              onChange={e => upd("ai_rewrite_descriptions", e.target.checked)}/>
            <span className="text-sm font-black">AI-rewrite vendor descriptions for the client PDF</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Service Call charge $">
              <input type="number" min="0" step="0.01"
                value={p.trip_charge_dollars ?? 65}
                onChange={e => upd("trip_charge_dollars", e.target.value)}
                data-testid="default-trip-charge"
                className={inputCls + " font-mono"}/>
            </Field>
            <Field label="Labor ratio (of remainder)">
              <input type="number" min="0" max="1" step="0.01"
                value={p.split_labor_ratio ?? 0.8}
                onChange={e => upd("split_labor_ratio", e.target.value)}
                data-testid="default-split-labor"
                className={inputCls + " font-mono"}/>
            </Field>
            <Field label="Materials ratio (of remainder)">
              <input type="number" min="0" max="1" step="0.01"
                value={p.split_materials_ratio ?? 0.2}
                onChange={e => upd("split_materials_ratio", e.target.value)}
                data-testid="default-split-materials"
                className={inputCls + " font-mono"}/>
            </Field>
          </div>
        </div>
      </Section>

      <Section title="Company info (top of PDF)">
        <div className="space-y-3">
          <Field label="Company name">
            <input value={p.company_name || ""} onChange={e => upd("company_name", e.target.value)}
              data-testid="default-company-name" className={inputCls}/>
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Line 2 (address / suite)">
              <input value={p.company_line2 || ""} onChange={e => upd("company_line2", e.target.value)}
                data-testid="default-company-line2" className={inputCls}/>
            </Field>
            <Field label="Line 3 (city / phone / email)">
              <input value={p.company_line3 || ""} onChange={e => upd("company_line3", e.target.value)}
                data-testid="default-company-line3" className={inputCls}/>
            </Field>
          </div>
          <Field label="Remit to (payment mailing address)">
            <input value={p.remit_to || ""} onChange={e => upd("remit_to", e.target.value)}
              data-testid="default-remit-to" className={inputCls}/>
          </Field>
          <Field label="Footer text (payment terms / note)">
            <textarea value={p.notes_footer || ""} onChange={e => upd("notes_footer", e.target.value)}
              rows={2} data-testid="default-notes-footer" className={inputCls}/>
          </Field>
        </div>
      </Section>

      <div className="flex justify-end">
        <button onClick={save} disabled={busy}
          data-testid="save-default-btn"
          className="h-11 px-6 border-2 border-[#0f172a] bg-[#00C4CC] hover:bg-[#00a8af] text-[#0f172a] hover:text-white font-black uppercase tracking-widest shadow-[3px_3px_0px_#0f172a] disabled:opacity-50">
          {busy ? "Saving…" : "Save Defaults"}
        </button>
      </div>
    </>
  );
}

function PortfolioOverrides() {
  const [data, setData] = useState(null); // { portfolios, tenant_default }
  const [openId, setOpenId] = useState(null);

  const load = () => {
    api.get("/pmr-billing/portfolio-overrides")
      .then(r => setData(r.data))
      .catch(() => toast.error("Couldn't load portfolios"));
  };
  useEffect(load, []);

  if (!data) return <div className="text-slate-500">Loading…</div>;

  return (
    <Section title={`Portfolios (${data.portfolios.length})`}>
      <p className="text-xs text-slate-500 mb-3">
        An override only fills the fields you set — the others fall back to tenant defaults.
        Leave a field blank to clear that override.
      </p>
      <div className="space-y-2">
        {data.portfolios.length === 0 && (
          <div className="p-4 text-center text-sm text-slate-400 italic">No portfolios yet.</div>
        )}
        {data.portfolios.map(p => (
          <PortfolioRow
            key={p.id}
            portfolio={p}
            tenantDefault={data.tenant_default}
            open={openId === p.id}
            onToggle={() => setOpenId(openId === p.id ? null : p.id)}
            onSaved={load}
          />
        ))}
      </div>
    </Section>
  );
}

function PortfolioRow({ portfolio: p, tenantDefault, open, onToggle, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      const o = p.override || {};
      const seeded = {};
      CAT_FIELDS.forEach(f => { seeded[f.key] = o[f.key] ?? ""; });
      seeded.min_charge_dollars = o.min_charge_cents != null ? o.min_charge_cents / 100 : "";
      seeded.trip_charge_dollars = o.trip_charge_cents != null ? o.trip_charge_cents / 100 : "";
      seeded.split_labor_ratio = o.split_labor_ratio ?? "";
      seeded.split_materials_ratio = o.split_materials_ratio ?? "";
      setForm(seeded);
    }
  }, [open, p.override]);

  const hasOverride = Object.keys(p.override || {}).length > 0;

  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      CAT_FIELDS.forEach(f => {
        const raw = form[f.key];
        if (raw !== "" && raw != null && !isNaN(Number(raw))) payload[f.key] = Number(raw);
      });
      if (form.min_charge_dollars !== "" && !isNaN(Number(form.min_charge_dollars))) {
        payload.min_charge_cents = Math.round(Number(form.min_charge_dollars) * 100);
      }
      if (form.trip_charge_dollars !== "" && !isNaN(Number(form.trip_charge_dollars))) {
        payload.trip_charge_cents = Math.round(Number(form.trip_charge_dollars) * 100);
      }
      if (form.split_labor_ratio !== "" && !isNaN(Number(form.split_labor_ratio))) {
        payload.split_labor_ratio = Number(form.split_labor_ratio);
      }
      if (form.split_materials_ratio !== "" && !isNaN(Number(form.split_materials_ratio))) {
        payload.split_materials_ratio = Number(form.split_materials_ratio);
      }
      await api.put(`/pmr-billing/portfolio-overrides/${p.id}`, payload);
      toast.success(`Override saved for ${p.name}`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const clearAll = async () => {
    if (!window.confirm(`Clear override for ${p.name}? Falls back to tenant defaults.`)) return;
    setBusy(true);
    try {
      await api.delete(`/pmr-billing/portfolio-overrides/${p.id}`);
      toast.success("Override cleared");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Clear failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="border-2 border-slate-200 rounded-lg bg-white" data-testid={`portfolio-row-${p.id}`}>
      <button onClick={onToggle} type="button"
        className="w-full flex items-center justify-between gap-3 p-3 hover:bg-slate-50"
        data-testid={`portfolio-toggle-${p.id}`}>
        <div className="text-left min-w-0 flex-1">
          <div className="font-black text-sm text-[#0B1F3A]">{p.name}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
            {p.landlord_name || "—"} · {p.unit_count} units
          </div>
        </div>
        {hasOverride ? (
          <span className="text-[10px] font-black uppercase tracking-widest bg-cyan-500 text-white px-2 py-1 rounded">
            {Object.keys(p.override).length} override{Object.keys(p.override).length === 1 ? "" : "s"}
          </span>
        ) : (
          <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
            default
          </span>
        )}
        <span className="text-slate-400">{open ? "▼" : "▶"}</span>
      </button>

      {open && (
        <div className="border-t-2 border-slate-100 p-3 space-y-3 bg-slate-50">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {CAT_FIELDS.map(f => (
              <label key={f.key} className="block">
                <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-0.5">
                  {f.label} % <span className="text-slate-400 font-normal">({tenantDefault[f.key]})</span>
                </div>
                <input type="number" min="0" max="500" step="1"
                  value={form[f.key] ?? ""}
                  onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                  placeholder={String(tenantDefault[f.key])}
                  data-testid={`portfolio-${p.id}-${f.key}`}
                  className="w-full border-2 border-slate-300 rounded px-2 py-1 text-xs font-mono"/>
              </label>
            ))}
            <label className="block">
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-0.5">
                Min charge $ <span className="text-slate-400 font-normal">({(tenantDefault.min_charge_cents || 0) / 100})</span>
              </div>
              <input type="number" min="0" step="0.01"
                value={form.min_charge_dollars ?? ""}
                onChange={e => setForm(prev => ({ ...prev, min_charge_dollars: e.target.value }))}
                placeholder={String((tenantDefault.min_charge_cents || 0) / 100)}
                data-testid={`portfolio-${p.id}-min`}
                className="w-full border-2 border-slate-300 rounded px-2 py-1 text-xs font-mono"/>
            </label>
            <label className="block">
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-0.5">
                Service Call $ <span className="text-slate-400 font-normal">({(tenantDefault.trip_charge_cents || 0) / 100})</span>
              </div>
              <input type="number" min="0" step="0.01"
                value={form.trip_charge_dollars ?? ""}
                onChange={e => setForm(prev => ({ ...prev, trip_charge_dollars: e.target.value }))}
                placeholder={String((tenantDefault.trip_charge_cents || 0) / 100)}
                data-testid={`portfolio-${p.id}-trip`}
                className="w-full border-2 border-slate-300 rounded px-2 py-1 text-xs font-mono"/>
            </label>
            <label className="block">
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-0.5">
                Labor ratio <span className="text-slate-400 font-normal">({tenantDefault.split_labor_ratio ?? 0.8})</span>
              </div>
              <input type="number" min="0" max="1" step="0.01"
                value={form.split_labor_ratio ?? ""}
                onChange={e => setForm(prev => ({ ...prev, split_labor_ratio: e.target.value }))}
                placeholder={String(tenantDefault.split_labor_ratio ?? 0.8)}
                data-testid={`portfolio-${p.id}-labor-ratio`}
                className="w-full border-2 border-slate-300 rounded px-2 py-1 text-xs font-mono"/>
            </label>
            <label className="block">
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-0.5">
                Materials ratio <span className="text-slate-400 font-normal">({tenantDefault.split_materials_ratio ?? 0.2})</span>
              </div>
              <input type="number" min="0" max="1" step="0.01"
                value={form.split_materials_ratio ?? ""}
                onChange={e => setForm(prev => ({ ...prev, split_materials_ratio: e.target.value }))}
                placeholder={String(tenantDefault.split_materials_ratio ?? 0.2)}
                data-testid={`portfolio-${p.id}-materials-ratio`}
                className="w-full border-2 border-slate-300 rounded px-2 py-1 text-xs font-mono"/>
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            {hasOverride && (
              <button onClick={clearAll} disabled={busy}
                data-testid={`portfolio-${p.id}-clear`}
                className="h-8 px-3 border-2 border-slate-300 hover:border-red-500 hover:text-red-600 text-slate-600 text-[10px] font-black uppercase tracking-widest disabled:opacity-50">
                Clear override
              </button>
            )}
            <button onClick={save} disabled={busy}
              data-testid={`portfolio-${p.id}-save`}
              className="h-8 px-4 border-2 border-[#0f172a] bg-[#00C4CC] hover:bg-[#00a8af] text-[#0f172a] hover:text-white text-[10px] font-black uppercase tracking-widest disabled:opacity-50">
              {busy ? "…" : "Save override"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full border-2 border-slate-300 rounded px-2 py-1.5 text-sm";

function Section({ title, children }) {
  return (
    <section className="bg-white border-2 border-[#0f172a] rounded-xl p-5 shadow-[6px_6px_0px_#0f172a] mb-5">
      <h2 className="font-display font-black text-lg text-[#0B1F3A] mb-3">{title}</h2>
      {children}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}
